import differenceInYears from 'date-fns/differenceInYears';
import validate from 'validate.js';

import parseDate from './parseDate';
import toDate from './toDate';
import toISODateString from './toISODateString';

// Used for custom error message labels
validate.validators.label = () => null;

validate.validators.checked = (value) => (value ? null : 'must be checked');

// Add support for arbitrary validators.
validate.validators.custom = (value, options, key, attributes) => {
  if (!options.validate) {
    throw new Error('Custom validator must implement a `validate` method.');
  }
  return options.validate(value, options, key, attributes);
};

validate.validators.age = (value, { minimum, maximum = 120, ...options }) => {
  const date = parseDate(value);
  const now = new Date();
  if (date == null || now - date < 0) {
    const error = options.message || options.notValid || 'must be a valid date';
    return validate.format(error, { value });
  }
  const age = differenceInYears(now, date);
  if (minimum && age < minimum) {
    const error = options.tooYoung || 'must be older than %{count}';
    return validate.format(error, { count: minimum });
  }
  if (maximum && age > maximum) {
    const error = options.tooOld || 'must be younger than %{count}';
    return validate.format(error, { count: maximum });
  }
  return null;
};

function uncapitalize(value) {
  if (typeof value !== 'string') {
    return value;
  }
  return value[0].toLowerCase() + value.slice(1);
}

function addCharToSkipLabelPrefix(value) {
  if (typeof value !== 'string' || value[0] === '^') {
    return value;
  }
  return `^${value}`;
}

function transformErrors(errors, fn) {
  if (validate.isArray(errors)) {
    return errors.map(fn);
  }
  return fn(errors);
}

validate.validators.serverErrors = (value, options, key, attributes) => {
  const { serverErrors } = attributes;
  if (!serverErrors) {
    return null;
  }
  let skipLabelPrefix = false;
  if (typeof options === 'object') {
    key = options.key || key;
    // eslint-disable-next-line prefer-destructuring
    skipLabelPrefix = options.skipLabelPrefix;
  }
  if (skipLabelPrefix) {
    return transformErrors(serverErrors[key], addCharToSkipLabelPrefix);
  }
  return transformErrors(serverErrors[key], uncapitalize);
};

validate.extend(validate.validators.datetime, {
  parse(value) {
    return toDate(value);
  },
  format(value, { dateOnly }) {
    if (dateOnly) {
      return toISODateString(new Date(value));
    }
    return new Date(value).toISOString();
  },
});

// Override convertErrorMessages to remove default label formatting. This is required to add
// custom labels and still support the `^` convention of the error messages.
validate.convertErrorMessages = (errors, options = {}) =>
  errors.reduce((messages, errorInfo) => {
    let error = validate.result(
      errorInfo.error,
      errorInfo.value,
      errorInfo.attribute,
      errorInfo.options,
      errorInfo.attributes,
      errorInfo.globalOptions
    );
    // istanbul ignore next
    if (!validate.isString(error)) {
      messages.push(errorInfo);
      return messages;
    }
    error = validate.format(error, {
      value: validate.stringifyValue(errorInfo.value, options),
    });
    messages.push({ ...errorInfo, error });
    return messages;
  }, []);

// Validates using validate.js but rewrites the error messages to use custom labels.
export default async function validateWithLabels(values, validationSchema, options = {}) {
  try {
    return await validate.async(values, validationSchema, options);
  } catch (errors) {
    if (!errors || options.fullMessages === false) {
      throw errors;
    }
    Object.keys(errors).forEach((attribute) => {
      let { label } = validationSchema[attribute];
      if (!label) {
        label = validate.capitalize(validate.prettify(attribute));
      }
      errors[attribute] = errors[attribute].map((attributeError) => {
        if (attributeError[0] === '^') {
          return attributeError.slice(1);
        }
        return `${label} ${attributeError}`;
      });
    });
    throw errors;
  }
}
